import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MapPin, X } from 'lucide-react';

import { API } from '../config';

/**
 * Station search input with live autocomplete dropdown.
 * value/onChange operate on a { code, name } object (or null).
 */
const StationAutocomplete = ({ value, onChange, placeholder = 'Station name or code', icon: Icon = MapPin, testid = 'station-input' }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const tmr = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const display = value ? `${value.name} (${value.code})` : q;

  const handleType = (txt) => {
    setQ(txt);
    if (value) onChange(null);
    setOpen(true);
    if (tmr.current) clearTimeout(tmr.current);
    if (txt.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    tmr.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/trains/stations`, { params: { q: txt.trim() } });
        setResults(r.data?.results || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 220);
  };

  const pick = (s) => { onChange(s); setQ(''); setOpen(false); setResults([]); };
  const clear = () => { onChange(null); setQ(''); setResults([]); setOpen(false); };

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-2 border-2 border-gray-100 rounded-2xl focus-within:border-tp-violet px-3 transition-colors bg-white">
        <Icon size={18} className="text-tp-violet shrink-0" />
        <input
          value={display}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => { if (!value) setOpen(true); }}
          placeholder={placeholder}
          data-testid={testid}
          className="flex-1 border-0 outline-none h-11 text-base bg-transparent"
        />
        {(value || q) && (
          <button onClick={clear} data-testid={`${testid}-clear`} className="text-gray-400 hover:text-gray-600 shrink-0">
            <X size={16} />
          </button>
        )}
      </div>
      {open && (results.length > 0 || loading) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-2xl border border-tp-violet/15 shadow-xl max-h-64 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-4 py-3 text-sm text-gray-400">Searching…</div>}
          {!loading && results.map((s) => (
            <button
              key={s.code}
              onClick={() => pick(s)}
              data-testid={`${testid}-opt-${s.code}`}
              className="w-full text-left px-4 py-2.5 hover:bg-tp-violet-soft/50 flex items-center gap-3 border-b border-gray-50 last:border-0"
            >
              <span className="text-xs font-extrabold text-tp-violet bg-tp-violet-soft rounded-lg px-2 py-1 min-w-[52px] text-center">{s.code}</span>
              <span className="text-sm text-tp-ink font-medium truncate">{s.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StationAutocomplete;
